import { IClientData } from "../../models/ClientData";
import {
  IAuthState,
  IClientFacts,
  IJourneyFacts,
  IJourneySchema,
  IProductDefinition,
  IRouteRule,
  IToolDefinition,
} from "../types/journeyInput.types";
import {
  IUXJourney,
  IUXProduct,
  IUXSchema,
  IUXTool,
  TToolStatus,
  IRuleFailure,
} from "../types/journeyUxSchema.types";
import { toolDefinitions } from "../definitions/tools/toolDefinitions";
import {
  buildJourneyFacts,
  toRuleFacts,
  evaluateRuleConditions,
  mapClientDataToFacts as mapClientDataToFactsService,
} from "./journeyRulesService";
import { TRuleFacts } from "../../config/jsonRulesEngine";
import { getClientRootDefinition } from "../definitions/clients";

type StagedTool = {
  toolDef: IToolDefinition;
  toolId: string;
  completionOrder?: number;
  completed: boolean;
  heading?: string;
  resolvedRoute: string;
  sourceIndex: number;
};

// products are now driven from client definitions

export function mapClientDataToFacts(client: IClientData | null): IClientFacts {
  return mapClientDataToFactsService(client);
}

async function evaluateProduct(
  product: IProductDefinition,
  factsRecord: TRuleFacts,
  failures: IRuleFailure[]
): Promise<IUXProduct | null> {
  if (!product.available) return null;

  const productQualificationResult =
    product.qualificationRules !== true
      ? await evaluateRuleConditions(
          product.qualificationRules,
          factsRecord
        )
      : { passed: true, failures: [], passedFacts: [] };

  failures.push(...productQualificationResult.failures);
  if (!productQualificationResult.passed) return null;

  const journeyResults = await Promise.all(
    product.journeys.map((j) =>
      evaluateJourney(
        j,
        factsRecord,
        failures,
        productQualificationResult.passedFacts
      )
    )
  );

  const qualifiedJourneys = journeyResults
    .filter((j): j is IUXJourney => j !== null)
    .sort((a, b) => b.priority - a.priority);

  const [primaryJourney = null, nextBestJourney = null] = qualifiedJourneys;

  return {
    productId: product.productId,
    productName: product.productName,
    available: product.available,
    primaryJourney,
    nextBestJourney,
    qualifiedJourneys,
  };
}

async function evaluateJourney(
  journey: IJourneySchema,
  factsRecord: TRuleFacts,
  failures: IRuleFailure[],
  productPassedFacts: IRuleFailure[]
): Promise<IUXJourney | null> {
  if (!journey.available) return null;

  const journeyQualificationResult = journey.qualificationRules
      ? await evaluateRuleConditions(
        journey.qualificationRules,
        factsRecord
      )
    : { passed: true, failures: [], passedFacts: [] };

  failures.push(...journeyQualificationResult.failures);
  if (!journeyQualificationResult.passed) return null;

  let priorityBoostTotal = 0;
  const priorityPassedFacts: IRuleFailure[] = [];
  if (journey.priorityRules) {
    const ruleResults = await Promise.all(
      journey.priorityRules.map(({ boost = 5, justification, ...condition }) =>
        evaluateRuleConditions({ all: [condition as any] }, factsRecord).then((result) => ({
          result,
          boost,
          justification,
        }))
      )
    );
    for (const { result, boost, justification } of ruleResults) {
      failures.push(...result.failures);
      if (result.passed) {
        priorityBoostTotal += boost;
        priorityPassedFacts.push(
          ...result.passedFacts.map((f) => ({ ...f, justification: justification ?? f.justification }))
        );
      }
    }
  }

  const priority = journey.basePriority + priorityBoostTotal;

  const tools = await buildJourneyTools(journey, factsRecord, failures);
  const completedCount = tools.filter((t) => t.status === "completed").length;
  const totalTools = tools.length;

  const priorityJustifications = priorityPassedFacts
    .map((f) => f.justification)
    .filter((j): j is string => !!j);

  const justifications = [
    ...(journey.justifications ?? []),
    ...priorityJustifications,
  ];

  return {
    journeyId: journey.journeyId,
    journeyName: journey.journeyName,
    description: journey.description,
    tagLine: journey.tagLine,
    priority,
    progress: {
      percentage:
        totalTools > 0 ? Math.round((completedCount / totalTools) * 100) : 0,
      completedCount,
      totalTools,
      isComplete: totalTools > 0 && completedCount === totalTools,
    },
    tools,
    justifications,
  };
}

async function buildJourneyTools(
  journey: IJourneySchema,
  factsRecord: TRuleFacts,
  failures: IRuleFailure[]
): Promise<IUXTool[]> {
  const staged: StagedTool[] = [];

  for (let sourceIndex = 0; sourceIndex < journey.tools.length; sourceIndex++) {
    const toolRef = journey.tools[sourceIndex];
    const toolDef = toolDefinitions[toolRef.toolId];
    if (!toolDef) continue;

    if (toolRef.visibleWhen) {
      const { passed, failures: f } = await evaluateRuleConditions(
        toolRef.visibleWhen,
        factsRecord
      );
      failures.push(...f);
      if (!passed) continue;
    }

    const { passed: completed, failures: f } = await evaluateRuleConditions(
      toolDef.completedWhen,
      factsRecord
    );
    failures.push(...f);

    // Evaluate route rules in parallel, pick the first matching rule's route.
    // toolRef.routeRules (journey-level) take precedence over toolDef.routeRules (tool-level).
    const activeRouteRules = toolRef.routeRules ?? toolDef.routeRules;
    let resolvedRoute = toolDef.route;
    if (activeRouteRules && activeRouteRules.length > 0) {
      const routeResults = await Promise.all(
        activeRouteRules.map(async ({ conditions, route }: IRouteRule) => {
          const { passed, failures: rf } = await evaluateRuleConditions(conditions, factsRecord);
          failures.push(...rf);
          return { passed, route };
        })
      );
      resolvedRoute = routeResults.find((r) => r.passed)?.route ?? toolDef.route;
    }

    staged.push({
      toolDef,
      toolId: toolRef.toolId,
      completionOrder: toolRef.completionOrder,
      completed,
      resolvedRoute,
      sourceIndex,
    });
  }

  const orderedStaged = [...staged].sort((a, b) => {
    const aOrder = a.completionOrder ?? Number.MAX_SAFE_INTEGER;
    const bOrder = b.completionOrder ?? Number.MAX_SAFE_INTEGER;

    return aOrder - bOrder || a.sourceIndex - b.sourceIndex;
  });

  let foundCurrent = false;
  const tools = orderedStaged.map((t, idx): IUXTool => {
    const isCurrent = !t.completed && !foundCurrent;
    if (isCurrent) foundCurrent = true;

    const status: TToolStatus = t.completed
      ? "completed"
      : isCurrent
      ? "current"
      : "future";

    return {
      toolId: t.toolId,
      name: t.toolDef.name,
      icon: t.toolDef.icon,
      description: t.toolDef.description,
      route: t.resolvedRoute,
      heading: t.toolDef.heading,
      sequence: idx + 1,
      intendedOrder: 0, // assigned below
      status,
      isCurrent,
      canRevisit: false,
      canSkip: false,
      estimatedDuration: t.toolDef.estimatedDurationMinutes,
    };
  });

  const statusRank: Record<TToolStatus, number> = {
    completed: 0,
    current: 1,
    future: 2,
  };
  const sorted = [...tools].sort(
    (a, b) =>
      statusRank[a.status] - statusRank[b.status] || a.sequence - b.sequence
  );
  sorted.forEach((t, i) => {
    t.intendedOrder = i + 1;
  });

  return tools;
}

export async function runJourneyPlanner(
  clientData: IClientData | null,
  auth: IAuthState
): Promise<IUXSchema> {
  const clientConfig = getClientRootDefinition(clientData);
  const facts: IJourneyFacts = buildJourneyFacts(clientData, auth);
  const factsRecord = toRuleFacts(facts);
  const failedRules: IRuleFailure[] = [];
  const clientSource = clientData?.regSrc ?? null;
  const productRegistry: IProductDefinition[] = clientConfig.products;

  const productResults = await Promise.all(
    productRegistry.map((p) => evaluateProduct(p, factsRecord, failedRules))
  );

  return {
    clientSource,
    clientId: clientData?.id ?? null,
    generatedAt: new Date().toISOString(),
    products: productResults.filter((p): p is IUXProduct => p !== null),
    failedRules,
  };
}

